import {Alert, Button, Group, ScrollArea, Stack, Table, Text} from '@mantine/core';
import React from 'react';

import {Cell} from 'notebook/tables/constants/CellConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import getTestProps from 'test/lib/cypress/getTestProps';

import {
  canOverwrite,
  TableConflict,
  TableConflictReason,
  TableConflictResolution,
} from './mergeDraftIntoLive';

/** Where a conflicting cell sits, as the editor shows it. */
export interface TableConflictLabels {
  rowLabel: string;
  columnLabel: string;
}

export interface TableConflictPanelProps {
  /** Conflicts still waiting for a resolution. */
  conflicts: Array<TableConflict>;
  labelConflict: (conflict: TableConflict) => TableConflictLabels;
  /** Headers for the label columns. */
  rowHeader: string;
  columnHeader: string;
  onResolve: (resolutions: Array<[conflict: TableConflict, resolution: TableConflictResolution]>) => void;
}

const THEIR_VALUE_LABEL_BY_REASON: Record<TableConflictReason, string | null> = {
  changed: null,
  finalized: 'Submitted',
  'read-only': 'Read-only',
  deleted: 'Removed',
};

function formatCell(cell: Cell | null): string {
  if (cell == null) {
    return '';
  }
  const text = cell.typedValue != null ? formatTypedValue(cell.typedValue, cell) : cell.text;
  return text === '' ? 'Blank' : text;
}

/**
 * Lists the draft's edits that collided with changes made while it was being edited, and lets the user
 * keep the table's value ("Use theirs") or their own ("Overwrite"). Overwrite only applies to cells
 * someone else changed: a read-only or removed cell can't take the draft's value.
 */
export default function TableConflictPanel(props: TableConflictPanelProps): JSX.Element {
  const {conflicts, labelConflict, rowHeader, columnHeader, onResolve} = props;
  const eligible = conflicts.filter((conflict) => canOverwrite(conflict));
  const blocked = conflicts.filter((conflict) => !canOverwrite(conflict));

  const resolveAll = (targets: Array<TableConflict>, resolution: TableConflictResolution): void => {
    onResolve(targets.map((conflict) => [conflict, resolution]));
  };

  return (
    <Alert
      color="yellow"
      title="Some of your changes conflict with changes made while you were editing"
      {...getTestProps({component: 'TableConflictPanel', element: 'root'})}
    >
      <Stack spacing="sm">
        <Text size="sm">
          Choose which value to keep for each cell. Nothing is saved until every conflict is resolved.
        </Text>
        {/* @ts-expect-error This is a DefinitelyTyped bug https://github.com/DefinitelyTyped/DefinitelyTyped/discussions/69006 */}
        <ScrollArea.Autosize mah={240}>
          <Table fontSize="sm">
            <thead>
              <tr>
                <th>{rowHeader}</th>
                <th>{columnHeader}</th>
                <th>Their value</th>
                <th>Your value</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {conflicts.map((conflict) => {
                const {rowLabel, columnLabel} = labelConflict(conflict);
                const theirValue =
                  THEIR_VALUE_LABEL_BY_REASON[conflict.reason] ?? formatCell(conflict.liveCell);
                return (
                  <tr key={conflict.key} {...getTestProps({component: 'TableConflictPanel', element: 'row'})}>
                    <td>{rowLabel}</td>
                    <td>{columnLabel}</td>
                    <td>{theirValue}</td>
                    <td>{formatCell(conflict.draftCell)}</td>
                    <td>
                      <Group spacing="xs" position="right" noWrap={true}>
                        <Button
                          size="xs"
                          variant="secondary"
                          onClick={() => {
                            onResolve([[conflict, 'theirs']]);
                          }}
                          {...getTestProps({component: 'TableConflictPanel', element: 'use-theirs'})}
                        >
                          Use theirs
                        </Button>
                        {canOverwrite(conflict) && (
                          <Button
                            size="xs"
                            variant="secondary"
                            onClick={() => {
                              onResolve([[conflict, 'overwrite']]);
                            }}
                            {...getTestProps({component: 'TableConflictPanel', element: 'overwrite'})}
                          >
                            Overwrite
                          </Button>
                        )}
                      </Group>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </ScrollArea.Autosize>
        <Group spacing="sm">
          <Button
            size="xs"
            variant="secondary"
            onClick={() => {
              resolveAll(conflicts, 'theirs');
            }}
            {...getTestProps({component: 'TableConflictPanel', element: 'use-theirs-all'})}
          >
            Use theirs for all
          </Button>
          {eligible.length > 0 && (
            <Button
              size="xs"
              variant="secondary"
              onClick={() => {
                resolveAll(eligible, 'overwrite');
              }}
              {...getTestProps({component: 'TableConflictPanel', element: 'overwrite-all'})}
            >
              Overwrite all
            </Button>
          )}
          {blocked.length > 0 && (
            <Button
              size="xs"
              variant="secondary"
              onClick={() => {
                resolveAll(blocked, 'theirs');
              }}
              {...getTestProps({component: 'TableConflictPanel', element: 'discard-blocked'})}
            >
              Discard blocked edits
            </Button>
          )}
        </Group>
      </Stack>
    </Alert>
  );
}
